import React, { useState } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
  TextField, MenuItem, Button, Alert, CircularProgress, Chip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import RefreshIcon from "@mui/icons-material/Refresh";
import { NAVY, TEXT_SECONDARY, WARNING } from "../../../theme/tokens";
import { WidgetType } from "./types";
import { PresetZone } from "./presets";
import MiniLayoutPreview from "./MiniLayoutPreview";
import { useGenerateAiTemplateMutation } from "../../../Api/attoDeskApi";

/**
 * Feature-flagged "Create with AI" entry point (section 9). Sends the prompt
 * to the backend, which returns a VALIDATED, normalised template structure
 * (never persisted). We preview it, surface per-zone content recommendations
 * (section 10), and apply it through the normal preset flow only on confirm.
 *
 * When the backend has no provider configured it replies 503 and we show a
 * clear "not available" message — no AI is ever called from the UI directly.
 */

interface AiTemplateDialogProps {
  open: boolean;
  orientation: "landscape" | "portrait";
  onClose: () => void;
  onApply: (name: string, zones: PresetZone[]) => void;
}

interface ServerZone {
  name: string;
  widgetType: WidgetType;
  x: number; y: number; width: number; height: number; zIndex: number;
  settings?: Record<string, unknown>;
}

const BUSINESS_TYPES = ["general", "restaurant", "cafe", "retail", "corporate", "healthcare"];

// Section 10: what to tell the operator each empty zone still needs.
const recommendationFor = (z: ServerZone): string | null => {
  const s = z.settings ?? {};
  switch (z.widgetType) {
    case "video": return s.media_id ? null : "Upload your main promotion video here";
    case "image": return s.media_id ? null : "Select an image for this zone";
    case "qr": return s.content ? null : "Add your online-order QR code";
    case "menu_board": return "Connect this zone to your VM POS menu";
    case "text": return s.content ? null : "Enter today's message / offer";
    case "website": return s.url ? null : "Enter the web address to display";
    default: return null;
  }
};

const AiTemplateDialog: React.FC<AiTemplateDialogProps> = ({ open, orientation, onClose, onApply }) => {
  const [prompt, setPrompt] = useState("");
  const [businessType, setBusinessType] = useState("restaurant");
  const [zones, setZones] = useState<ServerZone[] | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [warnings, setWarnings] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [generate, { isLoading }] = useGenerateAiTemplateMutation();

  const aspect = orientation === "portrait" ? "9 / 16" : "16 / 9";

  const run = async () => {
    setError(null);
    try {
      const res: any = await generate({ prompt, orientation, business_type: businessType }).unwrap();
      const data = res?.data;
      setZones(Array.isArray(data?.zones) ? data.zones : []);
      setTemplateName(data?.name ?? "AI Template");
      setWarnings(Array.isArray(data?.warnings) ? data.warnings : []);
    } catch (e: any) {
      setZones(null);
      const errs: string[] | undefined = e?.data?.data?.errors;
      setError(
        errs?.length
          ? `Could not use this result: ${errs.join(" ")}`
          : e?.data?.message || "The AI template assistant is not available right now.",
      );
    }
  };

  const toPresetZones = (list: ServerZone[]): PresetZone[] =>
    list.map((z) => ({
      name: z.name,
      widgetType: z.widgetType,
      x: z.x, y: z.y, width: z.width, height: z.height,
      zIndex: z.zIndex ?? 0,
      config: z.settings ?? {},
    }));

  const close = () => {
    setZones(null);
    setWarnings([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={close} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <AutoAwesomeIcon /> Create with AI
        <Chip size="small" label="Beta" sx={{ ml: 1, height: 20, fontSize: 10 }} />
      </DialogTitle>
      <DialogContent dividers>
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 1.5 }}>
          Describe your screen and we'll propose a layout. The result is
          validated and shown for review — nothing is saved until you apply it.
        </Typography>

        <TextField
          fullWidth multiline minRows={3} label="Describe your screen"
          placeholder="e.g. A restaurant promotion screen with one large food video, two offer images, a QR code and a scrolling special-offer banner."
          value={prompt} onChange={(e) => setPrompt(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", mb: 1 }}>
          <TextField select size="small" label="Business Type" sx={{ minWidth: 180 }}
            value={businessType} onChange={(e) => setBusinessType(e.target.value)}>
            {BUSINESS_TYPES.map((b) => (
              <MenuItem key={b} value={b}>{b.charAt(0).toUpperCase() + b.slice(1)}</MenuItem>
            ))}
          </TextField>
          <Typography sx={{ fontSize: 12, color: TEXT_SECONDARY }}>
            Orientation: {orientation === "portrait" ? "Portrait" : "Landscape"}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Button variant="contained" color="secondary" sx={{ color: NAVY }}
            disabled={isLoading || prompt.trim().length < 4}
            startIcon={isLoading ? <CircularProgress size={16} /> : <AutoAwesomeIcon />}
            onClick={run}>
            {zones ? "Regenerate" : "Generate Template"}
          </Button>
        </Box>

        {error && <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>}

        {zones && (
          <Box sx={{ mt: 2, display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 2 }}>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75 }}>
                Preview · {zones.length} zone{zones.length === 1 ? "" : "s"}
              </Typography>
              <MiniLayoutPreview zones={toPresetZones(zones)} aspect={aspect} showLabels />
              {warnings.length > 0 && (
                <Alert severity="info" sx={{ mt: 1, fontSize: 12 }}>
                  {warnings.map((w, i) => <div key={i}>{w}</div>)}
                </Alert>
              )}
            </Box>
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: TEXT_SECONDARY, mb: 0.75 }}>
                Content needed
              </Typography>
              {zones.map((z, i) => {
                const rec = recommendationFor(z);
                return (
                  <Box key={i} sx={{ mb: 0.75 }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: NAVY }}>
                      {z.name}
                    </Typography>
                    {rec && (
                      <Typography sx={{ fontSize: 12, color: WARNING }}>
                        {rec}
                      </Typography>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={close}>Cancel</Button>
        {zones && (
          <Button startIcon={<RefreshIcon />} onClick={run} disabled={isLoading}>
            Regenerate
          </Button>
        )}
        <Button variant="contained" color="secondary" sx={{ color: NAVY }}
          disabled={!zones || zones.length === 0}
          onClick={() => zones && onApply(templateName, toPresetZones(zones))}>
          Apply Template
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AiTemplateDialog;
